import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, PenSquare, MessageCircle } from "lucide-react";

export default function CategoryPage() {
  const { categorySlug } = useParams();
  const [category, setCategory] = useState(null);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [{ data: cats }, { data: tps }] = await Promise.all([
        api.get("/categories"),
        api.get("/topics", { params: { category: categorySlug } }),
      ]);
      setCategory(cats.find((c) => c.slug === categorySlug));
      setTopics(tps);
      setLoading(false);
    })();
  }, [categorySlug]);

  return (
    <div className="k-container py-12 md:py-16">
      <Link to="/forum" className="k-btn-ghost text-sm" data-testid="back-to-forum">
        <ArrowLeft className="mr-1 h-4 w-4" /> Tüm kategoriler
      </Link>
      <div className="mt-4 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="font-display text-5xl text-ink-900 md:text-6xl" data-testid="category-title">
            {category?.name || "…"}
          </h1>
          <p className="mt-2 max-w-2xl font-body text-ink-700">{category?.description}</p>
        </div>
        {user && (
          <Link
            to="/topic-new"
            state={{ category: categorySlug }}
            className="k-btn-primary"
            data-testid="category-new-topic-btn"
          >
            <PenSquare className="mr-2 h-4 w-4" /> Yeni konu aç
          </Link>
        )}
      </div>

      <div className="mt-10 space-y-3">
        {loading && [1, 2, 3].map((i) => <div key={i} className="k-card h-24 animate-pulse" />)}
        {!loading && topics.length === 0 && (
          <div className="k-card p-10 text-center" data-testid="category-empty">
            <p className="font-body text-ink-700">Henüz konu yok. İlk cümleyi sen kur.</p>
            {user && (
              <Link
                to="/topic-new"
                state={{ category: categorySlug }}
                className="k-btn-primary mt-6 inline-flex"
                data-testid="category-empty-cta"
              >
                <PenSquare className="mr-2 h-4 w-4" /> İlk konuyu aç
              </Link>
            )}
          </div>
        )}
        {!loading &&
          topics.map((t) => (
            <Link
              key={t.topic_id}
              to={`/topic/${t.topic_id}`}
              className="k-card flex items-center justify-between p-6 transition-all hover:-translate-y-0.5 hover:shadow-warm"
              data-testid={`topic-row-${t.topic_id}`}
            >
              <div className="min-w-0">
                <h3 className="font-heading text-lg font-bold text-ink-900 truncate">{t.title}</h3>
                <p className="mt-1 font-body text-sm text-ink-500">
                  {t.author_name} · {new Date(t.created_at).toLocaleDateString("tr-TR")}
                </p>
              </div>
              <span className="ml-4 inline-flex shrink-0 items-center gap-2 rounded-full bg-cream-200 px-3 py-1 text-sm text-ink-700">
                <MessageCircle className="h-4 w-4" /> {t.reply_count}
              </span>
            </Link>
          ))}
      </div>
    </div>
  );
}
